/**
 * Drawing Calculations Utility
 * Provides functions for calculating distances, areas, perimeters, and other geometric measurements
 */

import {TranslateService} from '@ngx-translate/core';

/**
 * Convert degrees to radians
 */
export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in kilometers
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate perimeter of polygon given an array of points
 * Returns perimeter in kilometers
 */
export function calculatePerimeter(points: Array<{ lat: number; lng: number }>): number {
  if (points.length < 2) return 0;

  let perimeter = 0;
  for (let i = 0; i < points.length - 1; i++) {
    perimeter += calculateDistance(points[i].lat, points[i].lng, points[i + 1].lat, points[i + 1].lng);
  }
  // Add distance from last point to first point to close the polygon
  if (points.length > 2) {
    perimeter += calculateDistance(
      points[points.length - 1].lat,
      points[points.length - 1].lng,
      points[0].lat,
      points[0].lng
    );
  }
  return perimeter;
}

/**
 * Calculate area of polygon using Shoelace formula
 * Returns area in square kilometers
 */
export function calculateAreaKm2(points: Array<{ lat: number; lng: number }>): number {
  if (points.length < 3) return 0;

  const R = 6371; // Earth's radius in kilometers
  let area = 0;

  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const xi = toRadians(points[i].lng) * Math.cos(toRadians(points[i].lat));
    const yi = toRadians(points[i].lat);
    const xj = toRadians(points[j].lng) * Math.cos(toRadians(points[j].lat));
    const yj = toRadians(points[j].lat);
    area += xi * yj - xj * yi;
  }

  area = Math.abs(area / 2.0) * R * R;
  return area;
}

/**
 * Calculate area in hectares
 * 1 km² = 100 hectares
 */
export function calculateAreaHa(points: Array<{ lat: number; lng: number }>): number {
  return calculateAreaKm2(points) * 100;
}

/**
 * Calculate centroid (center point) of polygon
 */
export function calculateCentroid(points: Array<{ lat: number; lng: number }>): { lat: number; lng: number } {
  if (points.length === 0) return { lat: 0, lng: 0 };

  let latSum = 0;
  let lngSum = 0;

  points.forEach(point => {
    latSum += point.lat;
    lngSum += point.lng;
  });

  return {
    lat: latSum / points.length,
    lng: lngSum / points.length
  };
}

/**
 * Format distance for display
 */
export function formatDistance(km: number, translate?:TranslateService): string {
  let mmLabel = 'mm';
  let mLabel = 'm';
  let kmLabel = 'km';
  if (translate) {
    mmLabel = translate.instant(mmLabel);
    mLabel = translate.instant(mLabel);
    kmLabel = translate.instant(kmLabel);
  }
  if (km < 0.001) {
    return `${(km * 1000000).toFixed(2)} ${mmLabel}`;
  } else if (km < 1) {
    return `${(km * 1000).toFixed(2)} ${mLabel}`;
  } else {
    return `${km.toFixed(2)} ${kmLabel}`;
  }
}

/**
 * Format area for display
 */
export function formatArea(km2: number): string {
  const ha = km2 * 100;
  if (ha < 0.01) {
    return `${(km2 * 1000000).toFixed(2)} m²`;
  } else if (ha < 1) {
    return `${ha.toFixed(4)} ha`;
  } else {
    return `${ha.toFixed(2)} ha (${km2.toFixed(4)} km²)`;
  }
}

/**
 * Format coordinates for display
 */
export function formatCoordinates(lat: number, lng: number): string {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}
